import { NgFor, NgIf } from '@angular/common';
import { Component, ViewChild, OnDestroy } from '@angular/core';
import { SwalComponent, SwalPortalTargets, SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { catchError, takeUntil } from 'rxjs/operators';
import { DataService } from '@adscore/adscore-frontend-common';
import { LogLevel, LogService, NEVER_TIMEOUT } from '@adscore/adscore-frontend-common';
import { StorageService } from '@adscore/adscore-frontend-common';
import { UserService } from '@adscore/adscore-frontend-common';
import { Subject } from 'rxjs';
import { LazyInjectService } from '../lazy-inject/lazy-inject.service';
import type { PhoneFormatterService } from '../phone-formatter/phone-formatter.service';
import { IError } from '@adscore/adscore-frontend-common';

@Component({
  standalone: true,
  imports: [SweetAlert2Module, NgIf, NgFor],
  providers: [LazyInjectService],
  template: `
    <swal
      #verifyPhoneNumberSwal
      title="Verify your phone number"
      icon="info"
      [showCancelButton]="true"
      confirmButtonColor="var(--adscore-primary)"
      confirmButtonText="Validate"
      cancelButtonText="Skip"
      [preConfirm]="preConfirmVerifyPhoneNumber"
      [allowOutsideClick]="false"
      (willClose)="showSkipPhoneNumberVerificationModal()"
      (willOpen)="runTimer()"
    >
      <ng-container *swalPortal="swalTargets.content">
        <div class="sa-custom-content text-left">
          <h5 class="text-secondary text-center fs-1 lh-1 p-b-1 m-b-1-25 b-b b-neutral-lighter">
            Please keep in mind:
          </h5>
          <ul class="bullets m-t-1 m-b-1 fs-0-8 lh-1-2 p-b-1 m-b-1-25 b-b b-neutral-lighter">
            <li>
              The verification code was sent to <strong>{{ this.phone }}</strong>
            </li>
            <li>It will arrive <strong>either via SMS or Phone Call</strong></li>
            <li>It might take a few minutes to arrive</li>
            <li>Type it in the field below and press Validate</li>
            <li>
              If you wish you can
              <a
                class="text-bold text-primary-dark"
                style="cursor: pointer"
                (click)="showSkipPhoneNumberVerificationModal()"
                >skip phone verification</a
              >, but then you won't get the 5$ bonus
            </li>
          </ul>
          <div class="input-component-container m-b-1-25">
            <div class="input-field required">
              <label for="phone-verification-code">Type Your Verification Code Here</label>
              <input
                id="phone-verification-code"
                class="d-block b-secondary-lighter"
                value=""
                type="text"
                autocomplete="off"
                autofocus
              />
            </div>
          </div>
          <p class="text-center text-underline text-bold" *ngIf="timerComplete" id="sms-resend">
            <a id="sms-resend-link" href="#" (click)="resend(); (false)"><u>Resend code</u></a>
          </p>
          <p class="text-center fs-0-9" id="sms-timer" *ngIf="!timerComplete">
            You can request another code in<br />
            <span id="sms-minutes" *ngIf="timerMinutesLabel">{{ timerMinutesLabel }}</span>
            <span id="minutes-seconds-separator" *ngIf="timerMinutesLabel && timerSecondsLabel">and</span>
            <span id="sms-seconds" *ngIf="timerSecondsLabel">{{ timerSecondsLabel }}</span>
          </p>
        </div>
      </ng-container>
    </swal>

    <swal
      #skipPhoneNumberVerificationSwal
      title="Are you sure?"
      icon="info"
      [showCancelButton]="true"
      confirmButtonColor="var(--adscore-primary)"
      confirmButtonText="Return to validation"
      cancelButtonText="Yes, skip validation"
      [preConfirm]="preSkipPhoneNumberVerification"
      [allowOutsideClick]="false"
      (willClose)="confirm(false)"
    >
      <ng-container *swalPortal="swalTargets.content">
        <div class="sa-custom-content fs-1 text-left">
          <p class="text-secondary-lighter text-bold m-b-1-25">
            Are you sure you want to skip phone number verification? If you do, you won't be granted $5 welcome bonus.
          </p>
        </div>
      </ng-container>
    </swal>

    <swal
      #errorSwal
      title="Verification Failed"
      icon="error"
      confirmButtonColor="var(--adscore-primary)"
      confirmButtonText="Close"
    >
      <ng-container *swalPortal="swalTargets.content">
        <div class="sa-custom-content fs-1 text-left">
          <p class="text-secondary-lighter text-bold m-b-1-25">There were errors while processing your registration:</p>
          <ul class="bullets">
            <li class="error" *ngFor="let err of errors" [innerHTML]="err.message"></li>
          </ul>
          <p class="fs-0-9">Please try again in a few minutes and contact support if the problem persists.</p>
        </div>
      </ng-container>
    </swal>

    <swal
      #successSwal
      [title]="successSwalTitle"
      icon="success"
      confirmButtonColor="var(--adscore-green)"
      confirmButtonText="Close"
    >
      <ng-container *swalPortal="swalTargets.content">
        <p class="fs-1 text-secondary-lighter text-bold m-b-1-25">Great to have you under our wing!</p>
        <p>You can now log in with your username and password using your device.</p>
      </ng-container>
    </swal>
  `
})
export class PhoneConfirmationComponent implements OnDestroy {
  readonly resendCooldown = 150;
  readonly tag = 'PhoneConfirmationComponent';
  timer: any;
  timeout: any;
  timerComplete = false;
  token: string;
  phone: string;

  timerMinutes: number;
  timerSeconds: number;

  @ViewChild('verifyPhoneNumberSwal', { static: true })
  private verifyPhoneNumberSwal: SwalComponent;
  @ViewChild('skipPhoneNumberVerificationSwal', { static: true })
  private skipPhoneNumberVerification: SwalComponent;

  @ViewChild('errorSwal', { static: true })
  private errorSwal: SwalComponent;
  errors: IError[] = [];

  @ViewChild('successSwal', { static: true })
  private successSwal: SwalComponent;
  successSwalTitle = '';

  private destroy$ = new Subject<boolean>();

  constructor(
    private dataService: DataService,
    private logService: LogService,
    private storageService: StorageService,
    private userService: UserService,
    public readonly swalTargets: SwalPortalTargets,
    private lazyInjectService: LazyInjectService
  ) {
    this.token = this.dataService.get('token');

    let savedResponse: any = this.storageService.get('emailTokenResponse');
    if (savedResponse) {
      savedResponse = JSON.parse(savedResponse);
    }
    if (savedResponse?.user) {
      this.setFormattedPhoneNumber(savedResponse.user.phone_number);
    }
  }

  async setFormattedPhoneNumber(phoneNumber: string) {
    const phoneFormatterService = await this.lazyInjectService.get<PhoneFormatterService>(() =>
      import('../phone-formatter/phone-formatter.service').then((m) => m.PhoneFormatterService)
    );

    this.phone = phoneFormatterService.formatNumber(phoneNumber);
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.stopTimer();
    this.logService.removeMessagesByTag(this.tag);
  }

  runTimer() {
    // reuse existing timer when user tried to skip phone number validation
    // and then decided to go back to verification
    this.timer = this.timer || new Date().getTime() + 1000 * this.resendCooldown;
    this.timerComplete = false;

    const step = () => {
      const diff = this.timer - new Date().getTime();

      if (diff < 1000) {
        this.timeout = null;
        this.timer = null;
        this.timerComplete = true;
        return;
      }

      this.timerMinutes = Math.floor(diff / 60000);
      this.timerSeconds = Math.floor((diff % 60000) / 1000);

      this.timeout = window.setTimeout(step, 50);
    };

    step();
  }

  stopTimer() {
    clearTimeout(this.timeout);
    this.timeout = null;
    this.timer = null;
    this.timerComplete = true;
  }

  showInitialModal() {
    this.verifyPhoneNumberSwal.fire();
  }

  preConfirmVerifyPhoneNumber = () => {
    this.confirm();
    return false;
  };

  showSkipPhoneNumberVerificationModal() {
    this.skipPhoneNumberVerification.fire();
  }

  preSkipPhoneNumberVerification = () => {
    this.showInitialModal();
    return false;
  };

  showErrorModal(errors: IError[]) {
    this.errors = errors;
    this.errorSwal.fire();
  }

  resend() {
    this.runTimer();
    this.userService
      .confirmEmailToken(this.token)
      .pipe(
        catchError((errors) => {
          this.stopTimer();
          this.showErrorModal(errors);
          return [];
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.logService.addMessageUnique(
          'Phone Verification',
          `Please check your phone, the verification code is on its way`,
          LogLevel.Success
        );
      });
  }

  confirm(validateCode = true) {
    const input = document?.getElementById('phone-verification-code');
    const code = input ? (<HTMLInputElement>input).value : '';

    this.logService.removeMessagesByTag(this.tag);

    if (validateCode && code.length === 0 && input) {
      input.classList.add('b-primary');
      this.logService.addMessageUnique(
        'Verification Code',
        `Please type in the Verification Code`,
        LogLevel.Error,
        NEVER_TIMEOUT,
        this.tag
      );
      return;
    }

    // make api call
    this.userService
      .confirmPhone(code)
      .pipe(
        catchError((errors) => {
          if (errors.length === 1) {
            const knownCodeErrors = [
              `Confirmation code not valid`,
              `The code provided does not match the expected value`
            ];
            if (knownCodeErrors.indexOf(errors[0].message) >= 0) {
              this.logService.addMessageUnique(
                'Verification Code',
                `Please make sure you typed the correct verification code.`,
                LogLevel.Error,
                NEVER_TIMEOUT,
                this.tag
              );
              return [];
            }
          }
          this.stopTimer();
          this.showErrorModal(errors);
          return [];
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.stopTimer();

        if (code) {
          this.successSwalTitle = `Phone verification successful`;
        } else {
          this.successSwalTitle = `Registration successful`;
        }
        this.successSwal.fire();
      });
  }

  get timerMinutesLabel() {
    return this.timerMinutes ? `${this.timerMinutes} ${this.timerMinutes > 1 ? 'minutes' : 'minute'}` : null;
  }

  get timerSecondsLabel() {
    return this.timerSeconds ? `${this.timerSeconds} ${this.timerSeconds > 1 ? 'seconds' : 'second'}` : null;
  }
}
